import {
    CalendarDate,
    CalendarDateTime,
    getLocalTimeZone,
    ZonedDateTime,
} from '@internationalized/date';
import {
    format as dateFnsFormat,
    formatDistance as fnsFormatDistance,
    type FormatOptions,
    type FormatDistanceOptions,
} from 'date-fns';
import { enUS as dateFnsEnUs } from 'date-fns/locale/en-US';
import { fr as dateFnsFr } from 'date-fns/locale/fr';
import type { CalendarDateString } from '$lib/utils/calendarDate';
import { fromCalendarDateString } from '$lib/utils/calendarDate';
import type { IsoDateString } from '$lib/utils/date.js';
import { fromIsoDateStringOrDate } from '$lib/utils/date.js';
import singleton from '$lib/utils/factory.js';
import { mergeObject } from '$lib/utils/mergeObject';
import type { ZonedDateTimeString } from '$lib/utils/zonedDateTime';
import { fromZonedDateTimeStringToLocalDate } from '$lib/utils/zonedDateTime';

export class IntlUtils {
    private readonly _numberFormat = singleton(() => new Intl.NumberFormat(this.locale));

    private readonly _displayNamesRegion = singleton(
        () => new Intl.DisplayNames([this.locale], { type: 'region' }),
    );

    private readonly _displayNamesLanguage = singleton(
        () => new Intl.DisplayNames([this.locale], { type: 'language' }),
    );

    constructor(private readonly locale: string = 'fr-FR') {}

    withLocale(locale: string): IntlUtils {
        return new IntlUtils(locale);
    }

    getLocale(): string {
        return this.locale;
    }

    getLang(): string {
        return this.locale.substring(0, 2);
    }

    formatInt(n: string | number | bigint): string {
        if (typeof n === 'string') {
            n = parseInt(n, 10);
        }
        return this._numberFormat().format(n);
    }

    formatPercent(
        n: string | number | bigint,
        options: Omit<Intl.NumberFormatOptions, 'style'> = {},
    ): string {
        if (typeof n === 'string') {
            n = parseInt(n, 10);
        }

        return new Intl.NumberFormat(this.locale, {
            ...options,
            style: 'percent',
        }).format(n);
    }

    dateFnsLocale() {
        if (this.locale.startsWith('fr')) {
            return dateFnsFr;
        }

        return dateFnsEnUs;
    }

    formatCalendarDate(
        date: CalendarDate | CalendarDateString,
        format: string,
        options: Omit<FormatOptions, 'locale'> = {},
    ): string {
        let effectiveDate;
        if (date instanceof CalendarDate) {
            effectiveDate = date.toDate(getLocalTimeZone());
        } else {
            effectiveDate = fromCalendarDateString(date).toDate(getLocalTimeZone());
        }

        return dateFnsFormat(effectiveDate, format, {
            locale: this.dateFnsLocale(),
            ...options,
        });
    }

    formatCalendarDateTime(
        date: CalendarDateTime,
        format: string,
        options: Omit<FormatOptions, 'locale'> = {},
    ): string {
        const effectiveDate = date.toDate(getLocalTimeZone());

        return dateFnsFormat(effectiveDate, format, {
            locale: this.dateFnsLocale(),
            ...options,
        });
    }

    formatZonedDate(
        date: ZonedDateTime | ZonedDateTimeString,
        format: string,
        options: Omit<FormatOptions, 'locale'> = {},
    ): string {
        let effectiveDate;
        if (date instanceof ZonedDateTime) {
            effectiveDate = date.toDate();
        } else {
            effectiveDate = fromZonedDateTimeStringToLocalDate(date).toDate();
        }

        return dateFnsFormat(effectiveDate, format, {
            locale: this.dateFnsLocale(),
            ...options,
        });
    }

    formatDate(
        date: Date | IsoDateString,
        format: string,
        options: Omit<FormatOptions, 'locale'> = {},
    ): string {
        return dateFnsFormat(fromIsoDateStringOrDate(date), format, {
            locale: this.dateFnsLocale(),
            ...options,
        });
    }

    countryLabel(country: string): string {
        if (country === '_africa' && this.locale.startsWith('en-')) {
            return 'Africa';
        } else if (country === '_africa') {
            return 'Afrique';
        }

        try {
            return this._displayNamesRegion().of(country.toUpperCase()) ?? country;
        } catch {
            return country;
        }
    }

    formatDistance(
        date: Date | IsoDateString | ZonedDateTime | CalendarDate,
        baseDate: Date | number,
        options: FormatDistanceOptions = {},
    ) {
        let effectiveDate: Date;
        if (date instanceof ZonedDateTime) {
            effectiveDate = date.toDate();
        } else if (date instanceof CalendarDate) {
            effectiveDate = date.toDate(getLocalTimeZone());
        } else {
            effectiveDate = fromIsoDateStringOrDate(date);
        }

        return fnsFormatDistance(effectiveDate, baseDate, {
            locale: this.dateFnsLocale(),
            ...options,
        });
    }

    formatCurrency(
        n: number,
        currency: string,
        options: Omit<Intl.NumberFormatOptions, 'currency'> = {},
    ) {
        const formatter = new Intl.NumberFormat(
            this.locale,
            mergeObject(
                {
                    notation: 'standard',
                    currency,
                    currencyDisplay: 'symbol',
                    style: 'currency',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                },
                options,
            ),
        );

        return formatter.format(n);
    }

    languageName(lang: string): string {
        try {
            return this._displayNamesLanguage().of(lang.replaceAll('_', '-')) ?? lang;
        } catch {
            return lang;
        }
    }
}
